// The download-service payloads for the conjoint blocks, ported from the coffee block (onExport /
// onMarketSimulatorExport). The block hands each one straight to the download service - the whole
// payload, including which file and export type to ask for, is resolved here.

import { getModelValue, getSurveyConfig, getSurveyTitle } from '@aytm/app/stats/lib/stat-page'
import { questionIdToPosition, questionPositionToId } from '@aytm/app/stats/page/components/lib/utils'
import { calcBCoefficients, calcNACoefficient } from '@aytm/app/stats/page/question-views/cjc/lib/calc'
import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

// The simulator ships the first seven packages of the list.
const EXPORTED_PACKAGES = 7

const MARKET_SIMULATOR_FILE_TYPE = 'marketSimulator2'

// An answer carries the engine version it was computed with; before that the survey's own says
// which one the question runs.
export const currentConfig = (answer, questionType) => answer?.config || getSurveyConfig(questionType) || { v: 1 }

// The link a virtual item points at wins over the question's own position.
const resolvePosition = (link, fallback) => (0 < link?.length && questionIdToPosition(link)) || fallback

export const calcDownloadParams = ({
  fileType,
  exportType,
  exportMethod,
  questionId,
  link,
  position,
  mode,
  persona
} = {}) => {
  const qid = (0 < position && questionPositionToId(position)) || questionId

  return {
    fileType,
    exportType,
    exportOptions: {
      position: resolvePosition(link, position),
      extra: exportMethod ? { method: exportMethod, qid } : { mode, cluster: persona, qid }
    }
  }
}

// The design sheet is generated rather than exported, so it goes out through its own service call.
export const calcDesignDownloadParams = ({ batches = [], designSheet, link, visualPosition } = {}) => ({
  position: resolvePosition(link, visualPosition),
  designSheet,
  params: {
    id: 'main',
    type: designSheet.conjointType[0],
    features: batches.filter(batch => !batch.na).map(batch => batch.text),
    featureLengths: designSheet.featureLengths,
    packagesPerScreen: designSheet.packagesPerScreen[0],
    hasNa: Boolean(designSheet.hasNA[0])
  }
})

// The two packages the simulator falls back to when the researcher built no list of their own.
const calcFallbackPackages = top10 => (2 < top10?.length ? [top10[1].slice(0, -1), top10[2].slice(0, -1)] : [])

const calcPackages = (packages, top10) =>
  packages.length ? packages.slice(0, EXPORTED_PACKAGES).map(entry => Array.from(entry)) : calcFallbackPackages(top10)

export const calcMarketSimulatorDownloadParams = ({
  fileType,
  exportType,
  questionType,
  questionKey,
  answer,
  batches = [],
  link,
  visualPosition,
  persona = 0,
  marketSimulatorPackages = [],
  marketSimulatorNA
} = {}) => {
  const isAggregate = 1 === currentConfig(answer, questionType).v
  const hasNA = 1 === answer.hasNA[0]

  const extra = {
    user: PageGlobals.USERNAME,
    hasNA,
    useNA: hasNA && Boolean(marketSimulatorNA),
    packages: calcPackages(
      marketSimulatorPackages,
      isAggregate ? answer.top10_aggregate || answer.top10 : answer.top10_individual
    )
  }

  if (isAggregate) {
    const scoring = batches.filter(batch => 0 < batch.answerVariants.length)

    extra.title = getSurveyTitle()
    extra.features = scoring.map(batch => batch.text)
    extra.options = scoring.map(batch => batch.answerVariants.map(variant => variant.text))
    extra.scores = (answer.featureLengths || []).map((_, i) => calcBCoefficients(answer, i + 1, persona))
    extra.NACoeff = calcNACoefficient(answer, persona)
  } else {
    extra.version = 2

    const filtersPattern = getModelValue('filtersPattern')
    if (0 < filtersPattern?.length) extra.filter = filtersPattern

    if (0 < persona && answer.clusterNames?.[persona - 1]) extra.cluster = persona
    extra.qid = `q${visualPosition}`
  }

  if (0 < persona && answer.clusterNames?.[persona - 1]) extra.persona = answer.clusterNames[persona - 1]

  return {
    fileType: isAggregate ? fileType : MARKET_SIMULATOR_FILE_TYPE,
    exportType,
    exportOptions: { position: resolvePosition(link, questionKey), extra }
  }
}
